import {
  ProviderRequestStatus,
  useGetFhirProviderByCodexIdLazyQuery,
  useRejectAppointmentRescheduleRequestMutation,
} from 'graphql/generated/remote-schema-hasura';
import { useCallback, useMemo, useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import {
  HORMONE_QUESTIONNAIRE_FULL,
  MY_APPOINTMENTS_COMPLETE_MY_VISIT_SUMMARY,
  MY_APPOINTMENTS_MY_VISIT_SUMMARY,
  MY_APPOINTMENTS_RESCHEDULE_AN_APPOINTMENT,
  MY_APPOINTMENTS_SCHEDULE_AN_APPOINTMENT,
  TEST_KITS_MANAGEMENT_SELECT_A_PROVIDER,
} from 'utilities/routes';
import { parseServiceType } from '../app/my-appointments/components/select-a-provider/ProviderCardProps';
import {
  APPOINTMENT_DETAILS,
  APPOINTMENT_STORAGE,
  BLOCK_PROVIDER,
  PROVIDER_APPOINTMENT_REQUEST,
} from '../app/my-appointments/components/scheduled-a-appointment/routePath';
import StorageService from '../utilities/storageService';
import { TestKitData } from '../components/dynamicQuestionnaire/interfaces/dynamicQuestionnaire.interface';
import { useGetFileFromStorage } from './useGetFileFromStorage';
import { openPdfOnMobileSafe } from '../utilities/fileHelpers';
import {
  ServiceOfferingsOutput,
  useGetProviderAvailabilityServiceTypesLazyQuery,
  useUpdateBecomePatientRequestStatusMutation,
} from 'graphql/generated/hasura';
import { IServiceTypes } from '../app/my-appointments/components/scheduled-a-appointment/interface';
import moment from 'moment';

export enum CustomNotificationActions {
  UPDATE_PROVIDER_REQUEST = 'UPDATE_PROVIDER_REQUEST',
  GO_TO_QUESTIONNAIRE = 'GO_TO_QUESTIONNAIRE',
  RESCHEDULED_PROVIDER_APPOINTMENT = 'RESCHEDULED_PROVIDER_APPOINTMENT',
  COMPLETE_VISIT_SUMMARY = 'COMPLETE_VISIT_SUMMARY',
  GO_TO_SCHEDULE_APPOINTMENT_WITH_PRESELECTED_PROVIDER = 'GO_TO_SCHEDULE_APPOINTMENT_WITH_PRESELECTED_PROVIDER',
  GO_TO_APPOINTMENT_SUMMARY = 'GO_TO_APPOINTMENT_SUMMARY',
  VIEW_IN_MAIL_DETAILS = 'VIEW_IN_MAIL_DETAILS',
  CHANGE_PROVIDER = 'CHANGE_PROVIDER',
  VIEW_TEST_KIT_RESULT = 'VIEW_TEST_KIT_RESULT',
  REJECT_APPOINTMENT_RESCHEDULE_REQUEST = 'REJECT_APPOINTMENT_RESCHEDULE_REQUEST',
  APPROVE_APPOINTMENT_REQUEST = 'APPROVE_APPOINTMENT_REQUEST',
  REJECT_APPOINTMENT_REQUEST = 'REJECT_APPOINTMENT_REQUEST',
  CHANGE_TEST_KIT_PROVIDER = 'CHANGE_TEST_KIT_PROVIDER',
}

interface RequestBecomePatientParams {
  itemReference: { reference: string; type?: string };
  date: string;
  patientFhirId: string;
  requestStatus: ProviderRequestStatus;
}

const appointmentStorage = new StorageService(
  APPOINTMENT_STORAGE,
  sessionStorage,
);
type CustomActionHandler = (
  customActionParams: Record<string, unknown>,
  fromAlert: boolean,
) => Promise<void>;

export interface useHandleExtraActionsType {
  handler: Record<CustomNotificationActions, CustomActionHandler>;
  isLoading: boolean;
  error?: string;
}

export function useHandleExtraActions(): useHandleExtraActionsType {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [updateBecomePatientRequest] =
    useUpdateBecomePatientRequestStatusMutation({});
  const [getProvider] = useGetFhirProviderByCodexIdLazyQuery();

  const { fetchFileFromStorage } = useGetFileFromStorage();

  const [rejectAppointmentRescheduleRequest] =
    useRejectAppointmentRescheduleRequestMutation();

  const [getProviderTypes] = useGetProviderAvailabilityServiceTypesLazyQuery();

  const getDownloadFunction = useCallback(
    async (diagnosticReportId: string, patientId: string) => {
      if (!diagnosticReportId || !patientId) return;
      const pdfLink = await fetchFileFromStorage(diagnosticReportId, patientId);
      openPdfOnMobileSafe(pdfLink);
    },
    [fetchFileFromStorage],
  );

  const handleAproveAppointmenRequest = useCallback(
    async (params: Record<string, unknown>) => {
      setIsLoading(true);
      try {
        const { provider, request } = params;
        const providerData = JSON.parse(provider as string);
        const requestData = JSON.parse(request as string);

        const {
          acuityCalendarId,
          acuityOwnerId,
          serviceOfferings,
          id: providerCodexId,
        } = providerData || {};

        const { proposedDate, appointmentTypeId, reason, id } =
          requestData || {};
        const date = moment(proposedDate)
          .utc(false)
          .startOf('day')
          .toISOString();

        const { data } = await getProviderTypes({
          variables: {
            providerCalendarId: acuityCalendarId,
          },
        });
        const serviceTypes =
          data?.GetProviderAvailabilityServiceTypes?.serviceTypes || [];
        const providerTypes: IServiceTypes[] = serviceTypes
          .filter(({ active }) => !!active)
          .map(
            (serviceTypes) =>
              ({
                id: serviceTypes.id,
                price: serviceTypes.price,
                duration: serviceTypes.duration,
                active: serviceTypes.active,
                name: serviceTypes.name,
                description: serviceTypes?.description,
                paddingAfter: serviceTypes.paddingAfter,
              } as IServiceTypes),
          );

        const serviceOfferingsAppointment = (serviceOfferings || []).map(
          (serviceOffering: ServiceOfferingsOutput) => ({
            price: serviceOffering.price,
            active: serviceOffering.active,
            serviceType: parseServiceType(serviceOffering.serviceType),
          }),
        );

        appointmentStorage.addData({
          selectedProvider: {
            codexProviderId: providerCodexId,
            calendarId: acuityCalendarId,
            ownerId: acuityOwnerId,
            serviceOfferings: serviceOfferingsAppointment,
          },
          selectedAppointment: {
            date,
            type: providerTypes.find(({ id }) => id === appointmentTypeId),
            timeSlot: moment(proposedDate).format('YYYY-MM-DDTHH:mm:ssZZ'),
          },
          selectedSymptoms: {
            reason: reason ?? '',
          },
          appointmentRequestId: id,
        });
        setTimeout(
          () =>
            navigate({
              pathname: `${MY_APPOINTMENTS_SCHEDULE_AN_APPOINTMENT}/${APPOINTMENT_DETAILS}`,
              search: createSearchParams({
                [BLOCK_PROVIDER]: 'true',
                [PROVIDER_APPOINTMENT_REQUEST]: 'true',
              }).toString(),
            }),
          200,
        );
      } catch (error) {
        setError((error as Error).message);
      } finally {
        setIsLoading(false);
      }
    },
    [getProviderTypes, navigate],
  );

  const handleRejectAppointmentRescheduleRequest = useCallback(
    async (params: Record<string, unknown>) => {
      try {
        setIsLoading(true);
        const response = await rejectAppointmentRescheduleRequest({
          variables: {
            input: {
              appointmentRescheduleRequestId: params.requestId as string,
              appointmentId: params.appointmentId as string,
            },
          },
        });

        if (response.errors) {
          throw new Error(response.errors[0].message);
        }

        setIsLoading(false);
      } catch (error) {
        setError((error as Error).message);
      }
    },
    [rejectAppointmentRescheduleRequest],
  );

  const handleRejectAppointmentProposedRequest = useCallback(
    async (params: Record<string, unknown>) => {
      try {
        setIsLoading(true);
        const response = await rejectAppointmentRescheduleRequest({
          variables: {
            input: {
              appointmentId: params.appointmentId as string,
              appointmentRescheduleRequestId: params.requestId as string,
            },
          },
        });

        if (response.errors) {
          throw new Error(response.errors[0].message);
        }

        setIsLoading(false);
      } catch (error) {
        setError((error as Error).message);
      }
    },
    [rejectAppointmentRescheduleRequest],
  );

  const handlePatientRequestUpdate = useCallback(
    async (params: Record<string, unknown>, fromAlert: boolean) => {
      try {
        const { itemReference, date, patientFhirId, requestStatus } =
          params as unknown as RequestBecomePatientParams;
        if (!itemReference || !date || !patientFhirId || !requestStatus) return;
        setIsLoading(true);
        const response = await updateBecomePatientRequest({
          variables: {
            patientFhirId: patientFhirId,
            requestStatus,
          },
        });

        if (response.errors) {
          throw new Error(response.errors[0].message);
        }

        setIsLoading(false);
        !fromAlert && navigate(-1);
      } catch (error) {
        setError((error as Error).message);
      }
    },
    [navigate, updateBecomePatientRequest],
  );

  const handleRescheduleAppointment = useCallback(
    async (params: Record<string, unknown>) => {
      navigate(
        `${MY_APPOINTMENTS_RESCHEDULE_AN_APPOINTMENT}/${params.appointmentCodexId}`,
      );
    },
    [navigate],
  );

  const handleViewTestKitResult = useCallback(
    async (params: Record<string, unknown>) => {
      const { resultReportPdfId: reportId, patientCodexId } = params;
      return getDownloadFunction(reportId as string, patientCodexId as string);
    },
    [getDownloadFunction],
  );

  const handleCompleteSummary = useCallback(
    async (params: Record<string, unknown>) => {
      navigate(
        `${MY_APPOINTMENTS_COMPLETE_MY_VISIT_SUMMARY}/${params.appointmentCodexId}`,
      );
    },
    [navigate],
  );
  const handleMySummary = useCallback(
    async (params: Record<string, unknown>) => {
      navigate(
        `${MY_APPOINTMENTS_MY_VISIT_SUMMARY}/${params.appointmentCodexId}`,
      );
    },
    [navigate],
  );

  const handleScheduledAppointment = useCallback(
    async (params: Record<string, unknown>, fromAlert: boolean) => {
      const { diagnosticReportId, providerCodexId } = params;
      setIsLoading(true);
      try {
        const provider = await getProvider({
          variables: { codexProviderId: providerCodexId as string },
        });
        if (provider.error) {
          throw new Error(provider.error.message);
        }
        const { acuityCalendarId, acuityOwnerId, serviceOfferings } =
          provider.data?.getFHIRProviderbyCodexIdArgument.providers[0]
            .providers || {};

        const serviceOfferingsAppointment = (serviceOfferings || []).map(
          (serviceOffering) => ({
            price: serviceOffering.price,
            active: serviceOffering.active,
            serviceType: parseServiceType(serviceOffering.serviceType),
          }),
        );

        appointmentStorage.addData({
          diagnosticReportIds: [diagnosticReportId],
          selectedProvider: {
            codexProviderId: providerCodexId,
            calendarId: acuityCalendarId,
            ownerId: acuityOwnerId,
            serviceOfferings: serviceOfferingsAppointment,
          },
        });
        setTimeout(
          () =>
            navigate({
              pathname: `${MY_APPOINTMENTS_SCHEDULE_AN_APPOINTMENT}/${APPOINTMENT_DETAILS}`,
            }),
          200,
        );
        !fromAlert && navigate(-1);
      } catch (error) {
        setError((error as Error).message);
      } finally {
        setIsLoading(false);
      }
    },
    [getProvider, navigate],
  );

  const handlerMap = useMemo<
    Record<CustomNotificationActions, CustomActionHandler>
  >(
    () => ({
      [CustomNotificationActions.VIEW_IN_MAIL_DETAILS]: async (params) => {
        const searchParams = new URLSearchParams({
          inMailId: params.inMailId as string,
        });
        navigate({
          search: `?${searchParams.toString()}`,
        });
      },
      [CustomNotificationActions.RESCHEDULED_PROVIDER_APPOINTMENT]:
        handleRescheduleAppointment,
      [CustomNotificationActions.VIEW_TEST_KIT_RESULT]: handleViewTestKitResult,
      [CustomNotificationActions.GO_TO_SCHEDULE_APPOINTMENT_WITH_PRESELECTED_PROVIDER]:
        handleScheduledAppointment,
      [CustomNotificationActions.UPDATE_PROVIDER_REQUEST]:
        handlePatientRequestUpdate,
      [CustomNotificationActions.COMPLETE_VISIT_SUMMARY]: handleCompleteSummary,
      [CustomNotificationActions.REJECT_APPOINTMENT_RESCHEDULE_REQUEST]:
        handleRejectAppointmentRescheduleRequest,
      [CustomNotificationActions.REJECT_APPOINTMENT_REQUEST]:
        handleRejectAppointmentProposedRequest,
      [CustomNotificationActions.GO_TO_APPOINTMENT_SUMMARY]: handleMySummary,
      [CustomNotificationActions.APPROVE_APPOINTMENT_REQUEST]:
        handleAproveAppointmenRequest,
      [CustomNotificationActions.GO_TO_QUESTIONNAIRE]: async (params) =>
        navigate(HORMONE_QUESTIONNAIRE_FULL, {
          state: {
            testKitData: params,
          },
        }),
      [CustomNotificationActions.CHANGE_PROVIDER]: async (params) => {
        navigate(TEST_KITS_MANAGEMENT_SELECT_A_PROVIDER, {
          state: {
            testKitWithClosedProviderIdNumber: (
              params.testKitData as TestKitData
            )?.testKitIdNumber,
            closedProvider: params.provider,
            testKitData: params.testKitData as TestKitData,
            questionnaireRequired: params.questionnaireRequired,
          },
        });
      },
      [CustomNotificationActions.CHANGE_TEST_KIT_PROVIDER]: async (params) => {
        navigate(TEST_KITS_MANAGEMENT_SELECT_A_PROVIDER, {
          state: {
            testKitWithClosedProviderIdNumber: params?.swabCode,
            closedProvider: params?.id,
          },
        });
      },
    }),
    [
      handleRescheduleAppointment,
      handleViewTestKitResult,
      handleScheduledAppointment,
      handlePatientRequestUpdate,
      handleCompleteSummary,
      handleRejectAppointmentRescheduleRequest,
      handleMySummary,
      handleAproveAppointmenRequest,
      handleRejectAppointmentProposedRequest,
      navigate,
    ],
  );

  return { handler: handlerMap, isLoading, error };
}
